import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Outlet, useParams } from 'react-router-dom';
import { LayoutDashboard, CalendarDays, MapPin, Store, MoreHorizontal, Loader2, Users, Wrench, Warehouse, Bed, BookOpen } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import type { RoleBadge } from '@/components/layout/AppTopbar';
import { AdminFabProvider } from '@/context/AdminFabContext';
import { AdminGlobalFab } from '@/components/admin/AdminGlobalFab';
import { useActiveMode } from '@/context/ActivePortalContext';
import { useAuth } from '@/context/AuthContext';
import { useMyProducers } from '@/hooks/useMyProducers';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { useProducerEffectiveFeatures } from '@/hooks/useProducerEffectiveFeatures';
import { useProducerCapabilities } from '@/hooks/useProducerCapabilities';
import { useMyBookings } from '@/hooks/useMyBookings';
import { useProducerPilot } from '@/hooks/useProducerPilot';
import { useAdminAttention } from '@/hooks/useAdminAttention';
import { customerNav } from '@/config/nav.customer';
import { editorNav } from '@/config/nav.editor';
import { getProducerNav, getProducerNavVisibility } from '@/config/nav.producer';
import { adminNav, getAdminProducerNav } from '@/config/nav.admin';
import { PRODUCER_FEATURE_NAV } from '@/config/producerFeatures';
import { isPast } from 'date-fns';
import type { NavConfig, NavItem } from '@/config/nav.types';
import { useProducerOfferings } from '@/hooks/useProducerOfferings';

/** Minimal producer nav when no farmId is available */
const PRODUCER_PORTAL_NAV: NavConfig = [
  {
    title: 'Värdportal',
    items: [
      {
        label: 'Översikt',
        href: '/app/producer',
        icon: LayoutDashboard,
        exact: true,
      },
    ],
  },
];

/**
 * Unified App Layout that wraps ALL logged-in views.
 * Provides consistent AppShell with context-aware navigation.
 * hideChrome + mobile footer are handled inside AppShell.
 */
export function UnifiedAppLayout() {
  const { isLoading: portalLoading, activeMode } = useActiveMode();
  const { user, isLoading: authLoading, rolesLoading } = useAuth();
  const { producers, isLoading: producersLoading } = useMyProducers({ enabled: activeMode !== 'admin' });

  const params = useParams<{ farmId?: string; producerId?: string }>();
  const farmId = params.farmId || params.producerId;

  const isProducerPortal = activeMode === 'host';
  const isProducerFarm = isProducerPortal && !!farmId;

  const { capabilities: roleCapabilities, isReadOnly } = useProducerAccess(
    isProducerFarm ? farmId : undefined
  );

  const { featureMap: effectiveFeatures, enabledFeatureKeys } = useProducerEffectiveFeatures(
    isProducerFarm ? farmId : undefined
  );

  const { meta: producerMeta, capabilities: capabilitiesMap } = useProducerCapabilities(
    isProducerFarm ? farmId : undefined
  );
  const { offerings: producerOfferingKeys } = useProducerOfferings(
    isProducerFarm ? farmId : undefined
  );
  const producerNavVisibility = useMemo(
    () => getProducerNavVisibility(producerOfferingKeys),
    [producerOfferingKeys]
  );

  const { bookings } = useMyBookings({ enabled: activeMode !== 'admin' });
  const { producerInterestCount, placeSuggestionCount } = useAdminAttention();
  const upcomingBookingsCount = useMemo(() => {
    return bookings.filter(
      (b) => b.status !== 'cancelled' && !isPast(new Date(b.event_start_date))
    ).length;
  }, [bookings]);

  const currentProducer = useMemo(() => {
    if (!farmId || activeMode !== 'host') return null;
    return producers.find(p => p.id === farmId) || null;
  }, [farmId, producers, activeMode]);

  // Pilot mode for producer portal
  const { isPilot, isFeatureVisible } = useProducerPilot();

  /** Filter nav items based on pilot mode */
  const filterPilotItems = (items: NavItem[]): NavItem[] => {
    if (!isPilot) return items;
    return items.filter(item => {
      if (!item.pilotFeatureKey) return true;
      return isFeatureVisible(item.pilotFeatureKey);
    });
  };

  // ── Nav config (sidebar – desktop) ─────────────────────────
  const navConfig = useMemo((): NavConfig => {
    let config: NavConfig;
    switch (activeMode) {
      case 'visitor':
        config = customerNav;
        break;
      case 'editor':
        config = editorNav;
        break;
      case 'host':
        config = farmId ? getProducerNav(farmId) : PRODUCER_PORTAL_NAV;
        break;
      case 'admin':
        config = farmId ? getAdminProducerNav(farmId) : adminNav;
        break;
      default:
        config = customerNav;
    }

    if (activeMode === 'host' && farmId) {
      config = config.map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.label === 'Aktiviteter') return producerNavVisibility.showActivities;
          if (item.label === 'Loppis') return producerNavVisibility.showFleaMarket;
          if (item.label === 'Gårdsbutik') return producerNavVisibility.showShop;
          if (item.label === 'Boende') return producerNavVisibility.showAccommodation;
          return true;
        }),
      }));
    }

    // Apply pilot filtering for producer portal
    if (isProducerPortal) {
      return config.map(section => ({
        ...section,
        items: filterPilotItems(section.items),
      }));
    }

    return config;
  }, [
    activeMode,
    farmId,
    isPilot,
    producerNavVisibility.showActivities,
    producerNavVisibility.showFleaMarket,
    producerNavVisibility.showShop,
    producerNavVisibility.showAccommodation,
  ]);

  const contextTitle = useMemo(() => {
    switch (activeMode) {
      case 'visitor': return 'Min sida';
      case 'editor': return 'Redaktion';
      case 'host': return currentProducer?.name || 'Värdportal';
      case 'admin': return 'Administration';
      default: return 'Farmly';
    }
  }, [activeMode, currentProducer]);

  // Fetch new place reports count for admin badge (not part of attention system)
  const { data: newPlaceReportsCount = 0 } = useQuery({
    queryKey: ['admin-place-reports-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('place_reports')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'new');
      if (error) return 0;
      return count ?? 0;
    },
    enabled: activeMode === 'admin',
    staleTime: 1000 * 60,
  });

  const badgeCounts = useMemo(() => {
    if (activeMode === 'visitor') {
      return { upcomingBookings: upcomingBookingsCount };
    }
    if (activeMode === 'admin') {
      return {
        newApplications: producerInterestCount,
        newPlaceSuggestions: placeSuggestionCount,
        newPlaceReports: newPlaceReportsCount,
      };
    }
    return {};
  }, [activeMode, upcomingBookingsCount, producerInterestCount, placeSuggestionCount, newPlaceReportsCount]);

  // Producer portal UX: Mobile footer
  // Strict 4-item rule: Översikt | Aktiviteter | [1 dynamic capability] | Mer
  // Dynamic slot priority: Boende > Gårdsbutik > Bokningar > Loppis
  const producerFooterItems = useMemo((): NavItem[] | undefined => {
    if (!isProducerFarm || !farmId) return undefined;
    const base = `/app/producer/farm/${farmId}`;

    // Dynamic capability slot — pick highest priority active offering
    const CAPABILITY_PRIORITY: Array<{
      key: keyof typeof producerNavVisibility;
      label: string;
      href: string;
      icon: typeof Bed;
    }> = [
      { key: 'showAccommodation', label: 'Boende', href: `${base}/accommodation`, icon: Bed },
      { key: 'showShop', label: 'Gårdsbutik', href: `${base}/shop`, icon: Store },
      { key: 'showFleaMarket', label: 'Loppis', href: `${base}/loppis`, icon: Warehouse },
    ];

    const dynamicSlot = CAPABILITY_PRIORITY.find(c => producerNavVisibility[c.key]);

    const items: NavItem[] = [
      { label: 'Översikt', href: base, icon: LayoutDashboard, exact: true },
      { label: 'Aktiviteter', href: `${base}/events`, icon: CalendarDays },
    ];

    if (dynamicSlot) {
      items.push({ label: dynamicSlot.label, href: dynamicSlot.href, icon: dynamicSlot.icon });
    }

    items.push({ label: 'Mer', href: `${base}/mer`, icon: MoreHorizontal });

    return items;
  }, [
    isProducerFarm,
    farmId,
    producerNavVisibility.showAccommodation,
    producerNavVisibility.showFleaMarket,
    producerNavVisibility.showShop,
  ]);

  // Admin portal UX: Footer = 5 items:
  // Översikt | Värdar | Platser | System | Mer
  const adminFooterItems = useMemo((): NavItem[] | undefined => {
    if (activeMode !== 'admin' || farmId) return undefined;
    return [
      { label: 'Översikt', href: '/app/admin/dashboard', icon: LayoutDashboard, exact: true, activeMatchPrefixes: ['/app/admin/control-panel'] },
      {
        label: 'Värdar', href: '/app/admin/producers', icon: Users,
        badgeKey: 'newApplications',
        activeMatchPrefixes: ['/app/admin/producer-interest', '/app/admin/producers/'],
      },
      {
        label: 'Platser', href: '/app/admin/places', icon: MapPin,
        activeMatchPrefixes: ['/app/admin/place-suggestions'],
      },
      {
        label: 'System', href: '/app/admin/system/host-debug', icon: Wrench,
        activeMatchPrefixes: ['/app/admin/system-logs', '/app/admin/email-outbox', '/app/admin/system/'],
      },
      {
        label: 'Mer', href: '/app/admin/experiences', icon: MoreHorizontal,
        activeMatchPrefixes: ['/app/admin/ambassadors', '/app/admin/legal', '/app/admin/cleanup'],
      },
    ];
  }, [activeMode, farmId]);

  // Filter feature nav items using effective features as single SoT.
  // When effective features resolve enabled=true, the item is shown — no pilot override.
  const filteredFeatureNav = useMemo(() => {
    if (!isProducerFarm) return [];

    return PRODUCER_FEATURE_NAV.filter(feat => {
      const eff = effectiveFeatures[feat.feature_key];
      const enabled = eff ? Boolean(eff.enabled) : false;

      if (!enabled) {
        if (import.meta.env.DEV) {
          console.debug(`[filteredFeatureNav] Hidden: ${feat.feature_key} – effective_enabled=false (source=${eff?.source ?? 'missing'})`);
        }
        return false;
      }

      return true;
    });
  }, [isProducerFarm, effectiveFeatures]);

  // ── Loading gate ───────────────────────────────────────────
  const isLoading = authLoading || rolesLoading || portalLoading || (activeMode !== 'admin' && producersLoading);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return null;

  const featureBaseHref = isProducerFarm ? `/app/producer/farm/${farmId}` : '';

  return (
    <AdminFabProvider>
      <AppShell
        navConfig={navConfig}
        role={({ visitor: 'customer', host: 'producer', editor: 'editor', admin: 'admin' } as Record<string, RoleBadge>)[activeMode] || 'customer'}
        contextTitle={contextTitle}
        currentProducerId={farmId}
        currentProducerName={currentProducer?.name}
        enabledCapabilities={enabledFeatureKeys}
        badgeCounts={badgeCounts}
        roleCapabilities={roleCapabilities}
        isViewerRole={isReadOnly}
        producerMeta={producerMeta}
        capabilitiesMap={capabilitiesMap}
        featureNavItems={filteredFeatureNav}
        effectiveFeatures={effectiveFeatures}
        featureBaseHref={featureBaseHref}
        footerOverride={producerFooterItems ?? adminFooterItems}
      >
        <Outlet />
      </AppShell>
      <AdminGlobalFab />
    </AdminFabProvider>
  );
}
